import React, { useContext } from 'react';
import cx from 'classnames';
import { uid } from 'uid';
import useThemeBlock from '@/hooks/useThemeBlock';
import { LocaleContext } from '@/contexts/locale-context';
import formBlock from '@/components/molecules/Form/block.json';
import block from './block.json';
// styles
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type InputEmailProps = InputProps & ThemeProps;

interface ForwardedInputEmail
	extends React.ForwardRefExoticComponent<
			InputEmailProps & React.RefAttributes<HTMLInputElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const InputEmail = React.forwardRef<HTMLInputElement, InputEmailProps>(
	(
		{
			id = uid(),
			label,
			name,
			placeholder,
			fullSize = true,
			required = false,
			disabled = false,
			invalid,
			inputAttributes,
			theme = {},
		},
		ref
	) => {
		const { blockTheme: formBlockTheme } = useThemeBlock(
			formBlock.slug,
			theme
		);

		const rootClass = cx(
			'supt-inputEmail supt-inputWrapper supt-inputField',
			fullSize ? 'supt-inputField--full' : 'supt-inputField--half',
			invalid && typeof invalid === 'string' && 'supt-inputField--error',
			getStyles(formBlockTheme)
		);
		const { __t } = useContext(LocaleContext);

		return (
			<div className={rootClass}>
				<label
					htmlFor={id}
					className="supt-inputEmail__label supt-inputField__label"
					data-optional={
						required ? '' : __t('form-input-optional', 'optional')
					}
				>
					{label}
				</label>
				<input
					id={id}
					className="supt-inputEmail__input supt-inputField__input"
					type="email"
					name={name}
					placeholder={placeholder}
					required={required}
					disabled={disabled}
					ref={ref}
					aria-invalid={!!invalid}
					{...inputAttributes}
				/>
				{invalid && typeof invalid === 'string' ? (
					<span
						role="alert"
						className="supt-inputEmail__error supt-inputField__error"
					>
						{invalid}
					</span>
				) : null}
			</div>
		);
	}
) as ForwardedInputEmail;

InputEmail.slug = block.slug;
InputEmail.title = block.title;
